// Focus trap for the two allowed dialogs (help sheet, bottom sheet). Tab and
// Shift Tab cycle inside the container; Escape calls onEscape. Returns the
// cleanup. Pure DOM so it can be unit tested under jsdom.

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function focusableIn(container: HTMLElement): readonly HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('hidden') && el.getAttribute('aria-hidden') !== 'true',
  );
}

export interface FocusTrapOptions {
  readonly onEscape?: () => void;
  // Element to focus first; defaults to the first focusable, then the container.
  readonly initialFocus?: HTMLElement | null;
}

// Where focus goes when the opener is gone, or when there was no opener at
// all. <body> is not an answer: focus resting there restarts the next Tab at
// the top of the document, which on this shell is a 256 stop sequence.
// Measured on 2026-08-20: pressing "?" from the workspace and then Escape left
// document.activeElement on <body>, because "?" is a document-level key and
// the trap dutifully restored the element that had focus, which was <body>.
function restoreTarget(previouslyFocused: HTMLElement | null): HTMLElement | null {
  if (previouslyFocused?.isConnected && previouslyFocused !== document.body) return previouslyFocused;
  return document.querySelector<HTMLElement>('[data-row-id][aria-selected="true"]')
    ?? document.querySelector<HTMLElement>('main[data-region="work"]');
}

export function trapFocus(container: HTMLElement, options: FocusTrapOptions = {}): () => void {
  const active = document.activeElement;
  const previouslyFocused = active instanceof HTMLElement && active !== document.body ? active : null;
  const first = options.initialFocus ?? focusableIn(container)[0] ?? container;
  first.focus();

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      options.onEscape?.();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = focusableIn(container);
    if (items.length === 0) {
      event.preventDefault();
      container.focus();
      return;
    }
    const head = items[0];
    const tail = items[items.length - 1];
    if (!head || !tail) return;
    const active = document.activeElement;
    if (event.shiftKey && (active === head || active === container)) {
      event.preventDefault();
      tail.focus();
    } else if (!event.shiftKey && active === tail) {
      event.preventDefault();
      head.focus();
    }
  };
  container.addEventListener('keydown', onKeyDown);
  return () => {
    container.removeEventListener('keydown', onKeyDown);
    restoreTarget(previouslyFocused)?.focus();
  };
}
